/**
 * Turn Watchdog — the client-owned terminator for the "busy" presentation
 * (connection-truth rail).
 *
 * Every path that flips a task's phase back to idle is an inbound socket
 * event; if the terminal event is lost (blip, missed broadcast, server
 * restart) the spinner runs forever. This watchdog is the level-triggered
 * backstop: while a turn looks live but no agent event has arrived for 30s
 * AND the connection is healthy, it re-READS (MESSAGE_SYNC_REQUEST) instead
 * of guessing — if the server says the turn is over, the existing sync path
 * clears the spinner; if the turn is genuinely long (silent tool run), the
 * sync is a cheap no-op and the probe backs off (30s → 60s cap).
 *
 * Unhealthy windows never probe: the Reconnecting presentation owns those,
 * and the reconnect burst already fires a sync on recovery.
 */
import { EVENTS } from "@/constants/events";
import { useTaskStore } from "./taskStore";
import { useConnectionHealthStore } from "./connectionHealthStore";
import { getSocket } from "./socketService";

export const WATCHDOG_SILENCE_MS = 30_000;
export const WATCHDOG_BACKOFF_MAX_MS = 60_000;
const SWEEP_INTERVAL_MS = 5_000;

// Module-level, not store state: the clock is stamped at wire rate (every
// delta) and only the 5s sweep ever reads it — reactivity would be waste.
const lastAgentEventAt = new Map<string, number>();
const nextProbeNotBefore = new Map<string, number>();
const probeBackoffMs = new Map<string, number>();

/** Any agent-stream event for this task resets its silence clock. */
export function noteAgentEvent(taskId: string): void {
  lastAgentEventAt.set(taskId, Date.now());
  nextProbeNotBefore.delete(taskId);
  probeBackoffMs.delete(taskId);
}

export interface WatchdogTaskView {
  taskId: string;
  phase: string;
  lastActivityAt?: Date | null;
}

export interface WatchdogDeps {
  tasks: () => Iterable<WatchdogTaskView>;
  health: () => "healthy" | "degraded" | "down";
  /** Emit a sync probe; return false when the socket can't carry it. */
  emitSyncRequest: (taskId: string) => boolean;
}

/** One sweep — exported pure-ish for tests; `startTurnWatchdog` wires reality. */
export function sweepOnce(now: number, deps: WatchdogDeps): void {
  if (deps.health() !== "healthy") return;
  const liveIds = new Set<string>();
  for (const t of deps.tasks()) {
    const busy = t.phase === "processing" || t.phase === "waiting";
    if (!busy) continue;
    liveIds.add(t.taskId);
    let last = lastAgentEventAt.get(t.taskId);
    if (last === undefined) {
      // First sighting of this live turn — start the clock from the task's own
      // last activity (send time) so a turn that died before its first event
      // still gets probed, but a fresh send isn't probed instantly.
      last = t.lastActivityAt ? t.lastActivityAt.getTime() : now;
      lastAgentEventAt.set(t.taskId, last);
    }
    if (now - last < WATCHDOG_SILENCE_MS) continue;
    if (now < (nextProbeNotBefore.get(t.taskId) ?? 0)) continue;
    if (!deps.emitSyncRequest(t.taskId)) continue;
    const backoff = probeBackoffMs.get(t.taskId) ?? WATCHDOG_SILENCE_MS;
    nextProbeNotBefore.set(t.taskId, now + backoff);
    probeBackoffMs.set(
      t.taskId,
      Math.min(backoff * 2, WATCHDOG_BACKOFF_MAX_MS)
    );
  }
  // Bound the maps: entries for tasks no longer live drop out here.
  for (const id of [...lastAgentEventAt.keys()]) {
    if (!liveIds.has(id)) {
      lastAgentEventAt.delete(id);
      nextProbeNotBefore.delete(id);
      probeBackoffMs.delete(id);
    }
  }
}

export function _resetWatchdogForTests(): void {
  lastAgentEventAt.clear();
  nextProbeNotBefore.clear();
  probeBackoffMs.clear();
}

export function startTurnWatchdog(): () => void {
  const timer = setInterval(() => {
    sweepOnce(Date.now(), {
      tasks: () =>
        [...useTaskStore.getState().tasks.values()].map((t) => ({
          taskId: t.taskId,
          phase: t.phase,
          lastActivityAt: t.lastActivityAt,
        })),
      health: () => useConnectionHealthStore.getState().health,
      emitSyncRequest: (taskId) => {
        const socket = getSocket();
        if (!socket?.connected) return false;
        socket.emit(EVENTS.MESSAGE_SYNC_REQUEST, { taskId });
        return true;
      },
    });
  }, SWEEP_INTERVAL_MS);
  return () => clearInterval(timer);
}
